import { HttpContextToken, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { runtimeConfig } from '../runtime-config';
import { canonicalRequestId, canonicalTraceId, canonicalUuid } from '../technical-identifiers';

export const SKIP_CLIENT_OBSERVABILITY = new HttpContextToken<boolean>(() => false);

export type ClientTelemetryOutcome = 'success' | 'error' | 'timeout' | 'cancelled';

export interface ClientTelemetryEvent {
  name: string;
  timestamp?: string;
  duration_ms?: number;
  outcome?: ClientTelemetryOutcome;
  error_type?: string;
  route?: string;
  request_id?: string;
  trace_id?: string;
  online?: boolean;
  details?: Record<string, string>;
}

interface QueuedTelemetryEvent {
  id: string;
  createdAt: number;
  attempts: number;
  nextAttemptAt: number;
  headers: Record<string, string>;
  event: ClientTelemetryEvent;
}

interface SchemaSplitBudget {
  windowStartedAt: number;
  attempts: number;
}

interface ConnectionInformation extends EventTarget {
  effectiveType?: string;
  type?: string;
}

const QUEUE_KEY = 'reforce_telemetry_queue_v2';
const LEGACY_QUEUE_KEY = 'reforce_telemetry_queue';
const FLUSH_LOCK_KEY = 'reforce_telemetry_flush_lock_v2';
const SCHEMA_SPLIT_BUDGET_KEY = 'reforce_telemetry_schema_split_budget_v2';
const MAX_QUEUE_LENGTH = 100;
const MAX_BATCH_LENGTH = 50;
const MAX_SCHEMA_SPLITS_PER_WINDOW = 8;
const SCHEMA_SPLIT_WINDOW_MS = 60_000;
const MAX_QUEUE_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_EVENT_DURATION_MS = 24 * 60 * 60 * 1000;
const SEND_TIMEOUT_MS = 8_000;
const SLOW_REQUEST_MS = 15_000;
const ALLOWED_NETWORK_VALUES = new Set([
  'unknown', 'offline', 'online', 'slow-2g', '2g', '3g', '4g',
  'wifi', 'ethernet', 'cellular', 'bluetooth', 'wimax', 'other', 'mixed', 'none',
]);
const ALLOWED_ERROR_TYPE = /^(?:offline|network_or_cors|network|dns|tls|timeout|cancelled|unknown|client_slow_request|client_cancelled|navigation_cancel|navigation_error|http_(?:[1-5][0-9]{2}|[45]xx)|token_(?:missing|invalid|expired|refresh_failed))$/;
const DETAIL_VALUE_PATTERNS: Readonly<Record<string, RegExp>> = {
  attempt: /^[0-9]{1,3}$/,
  branch: /^(userinfo|refresh|interactive_login|product_api)$/,
  stage: /^(initial_load|first_contentful_paint|angular_bootstrap|startup|auth|refresh|userinfo|product_api|before_app_ready|after_app_ready|app_ready_timeout|dns|connect|tls|ttfb|html_download|dom_content_loaded|load)$/,
  resource_type: /^(script|link|css|style|img|image|font|fetch|xmlhttprequest|navigation|other|unknown)$/,
  from: /^(unknown|offline|online|slow-2g|2g|3g|4g|wifi|ethernet|cellular|bluetooth|wimax|other|mixed|none)$/,
  to: /^(unknown|offline|online|slow-2g|2g|3g|4g|wifi|ethernet|cellular|bluetooth|wimax|other|mixed|none)$/,
  reason: /^(router_error|code_[0-9]{1,4}|unknown)$/,
  grant_type: /^(authorization_code|refresh_token)$/,
  state: /^(ready|recovered|visible|hidden|bfcache|pagehide)$/,
  visibility: /^(visible|hidden|prerender)$/,
  navigation_type: /^(initial|imperative|popstate|hashchange|reload|navigate|back_forward|prerender|unknown)$/,
  status: /^[0-5]?[0-9]{1,2}$/,
  trigger: /^(online|offline|connection_change|manual|startup|unknown)$/,
  method: /^(GET|POST|PUT|PATCH|DELETE|OPTIONS|HEAD)$/,
};

const ALLOWED_EVENT_NAMES = new Set([
  'app.started',
  'app.backgrounded',
  'app.foregrounded',
  'app.terminated',
  'network.changed',
  'startup.completed',
  'startup.timeout',
  'startup.failed',
  'startup.retry_clicked',
  'startup.stalled',
  'page.html_loaded',
  'page.performance',
  'angular.bootstrapped',
  'route.started',
  'route.rendered',
  'route.failed',
  'resource.failed',
  'resource.performance',
  'http.completed',
  'http.failed',
  'http.slow',
  'http.cancelled',
  'javascript.failed',
  'promise.rejected',
]);

const ALLOWED_AUTH_UI_ROUTES = new Set([
  '/',
  '/frontend',
  '/frontend-bundle',
  '/runtime-config.js',
  '/login',
  '/login/auth',
  '/login/registration',
  '/login/verify',
  '/auth/session/:session_id/start',
  '/auth/session/:session_id/verify',
  '/auth/session/:session_id/yandex',
  '/auth/session/:session_id/resend',
  '/auth/session/:session_id/documents',
  '/auth/session/:session_id/finalize',
  '/registration/session/:session_id/start',
  '/registration/session/:session_id/verify',
  '/registration/session/:session_id/resend',
  '/registration/session/:session_id/documents',
  '/registration/session/:session_id/finalize',
  '/unknown',
]);

export function normalizeTelemetryRoute(url: string, origin = window.location.origin): string {
  try {
    const pathname = new URL(url, origin).pathname;
    const normalized = pathname
      .replace(/\/(auth|registration)\/session\/[^/]+/gi, '/$1/session/:session_id')
      .replace(/\/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, '/:id')
      .replace(/\/\d+(?=\/|$)/g, '/:id')
      .replace(/([_-])[a-z0-9]{16,}(?=\.(?:js|css)$)/gi, '$1:hash');
    const candidate = normalized.split('/').map((segment) => {
      let decoded = segment;
      try { decoded = decodeURIComponent(segment); } catch { /* keep encoded */ }
      const digitCount = (decoded.match(/\d/g) ?? []).length;
      return decoded.includes('@') || digitCount >= 7 || segment.length > 96 ? ':id' : segment;
    }).join('/').slice(0, 256);
    if (/^\/(?:main|polyfills|styles|chunk-|scripts)[^/]*\.(?:js|css)$/i.test(candidate)) {
      return '/frontend-bundle';
    }
    return ALLOWED_AUTH_UI_ROUTES.has(candidate) ? candidate : '/unknown';
  } catch {
    return '/unknown';
  }
}

export function isRequestInsideApi(url: string, apiBase: string, origin = window.location.origin): boolean {
  try {
    const target = new URL(url, origin);
    const base = new URL(apiBase || '/', origin);
    if (target.origin !== base.origin) return false;
    const basePath = base.pathname.replace(/\/$/, '');
    return basePath === '' || basePath === '/' || target.pathname === basePath || target.pathname.startsWith(`${basePath}/`);
  } catch {
    return false;
  }
}

@Injectable({ providedIn: 'root' })
export class ClientObservabilityService {
  public readonly slowRequestMs = SLOW_REQUEST_MS;
  public readonly flowId = this.getFlowId();

  private readonly endpoint = this.resolveUrl(runtimeConfig.observabilityUrl);
  private readonly apiBase = this.resolveUrl(runtimeConfig.apiUrl || '/');
  private readonly deviceId = this.getOrCreateDeviceId();
  private readonly ownerId = this.newCorrelationId();
  private flushTimer: number | undefined;
  private flushing = false;
  private performanceCaptured = false;
  private previousNetwork = this.connectionType();
  private batchSize = MAX_BATCH_LENGTH;
  private memoryQueue: QueuedTelemetryEvent[] = [];
  private schemaSplitBudget: SchemaSplitBudget = { windowStartedAt: 0, attempts: 0 };

  constructor() {
    // V1 did not retain the original flow headers. Replaying it under a new flow would
    // create false correlations, so it is intentionally discarded during migration.
    this.removeStorage(LEGACY_QUEUE_KEY);
    window.__reforceObservability?.attach((candidate) => {
      if (this.isClientTelemetryEvent(candidate)) {
        this.report(candidate);
      }
    });
    this.installLifecycleObservers();
    this.scheduleFlush(100);
  }

  public headers(requestId?: string): Record<string, string> {
    const headers: Record<string, string> = {
      'X-Auth-Flow-ID': this.flowId,
      'X-Device-ID': this.deviceId,
      'X-Platform': 'web',
      'X-App-Version': runtimeConfig.release,
      'X-Network-Type': this.connectionType(),
      'X-Client-App': runtimeConfig.clientApp,
    };
    if (requestId) {
      headers['X-Request-ID'] = requestId;
    }
    return headers;
  }

  public newRequestId(): string {
    return this.newCorrelationId();
  }

  public isTelemetryRequest(url: string): boolean {
    return this.sameUrl(this.resolveUrl(url), this.endpoint);
  }

  public shouldObserveRequest(url: string): boolean {
    return !this.isTelemetryRequest(url) && isRequestInsideApi(url, this.apiBase.href);
  }

  public report(event: ClientTelemetryEvent): void {
    if (!runtimeConfig.telemetryEnabled || !ALLOWED_EVENT_NAMES.has(event.name)) {
      return;
    }
    const details = this.normalizeDetails(event.details);
    if (event.details && !details) return;
    const requestId = event.request_id ? canonicalRequestId(event.request_id) : null;
    const traceId = event.trace_id ? canonicalTraceId(event.trace_id) : null;
    const errorType = event.error_type && ALLOWED_ERROR_TYPE.test(event.error_type)
      ? event.error_type
      : event.error_type ? 'unknown' : undefined;
    const normalized: ClientTelemetryEvent = {
      ...event,
      timestamp: event.timestamp ?? new Date().toISOString(),
      online: event.online ?? navigator.onLine,
      ...(event.route ? { route: this.safeRoute(event.route) } : {}),
      ...(requestId ? { request_id: requestId } : { request_id: undefined }),
      ...(traceId ? { trace_id: traceId } : { trace_id: undefined }),
      ...(errorType ? { error_type: errorType } : { error_type: undefined }),
      ...(details ? { details } : { details: undefined }),
    };
    this.appendToQueue({
      id: this.newCorrelationId(),
      createdAt: Date.now(),
      attempts: 0,
      nextAttemptAt: 0,
      headers: this.headers(),
      event: normalized,
    });
    this.scheduleFlush(250);
  }

  public reportHttpFailure(
    url: string,
    method: string,
    startedAt: number,
    error: unknown,
    requestId: string,
  ): void {
    const response = error instanceof HttpErrorResponse ? error : null;
    const errorType = !navigator.onLine
      ? 'offline'
      : response?.status === 0
        ? 'network_or_cors'
        : response
          ? response.status >= 500
            ? 'http_5xx'
            : `http_${response.status}`
          : 'unknown';
    this.report({
      name: 'http.failed',
      outcome: 'error',
      error_type: errorType,
      route: this.safeRoute(url),
      duration_ms: this.elapsed(startedAt),
      request_id: response?.headers.get('X-Request-ID') ?? requestId,
      trace_id: response?.headers.get('X-Trace-ID') ?? undefined,
      details: {
        method: method.toUpperCase(),
        status: String(response?.status ?? 0),
      },
    });
  }

  public safeRoute(url: string): string {
    return normalizeTelemetryRoute(url);
  }

  public capturePerformance(): void {
    if (this.performanceCaptured || typeof performance.getEntriesByType !== 'function') {
      return;
    }
    const collect = (): void => {
      if (this.performanceCaptured) return;
      this.performanceCaptured = true;
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      if (navigation) {
        const values: Array<[string, number]> = [
          ['dns', navigation.domainLookupEnd - navigation.domainLookupStart],
          ['connect', navigation.connectEnd - navigation.connectStart],
          ['tls', navigation.secureConnectionStart > 0 ? navigation.connectEnd - navigation.secureConnectionStart : 0],
          ['ttfb', navigation.responseStart - navigation.requestStart],
          ['html_download', navigation.responseEnd - navigation.responseStart],
          ['dom_content_loaded', navigation.domContentLoadedEventEnd - navigation.startTime],
          ['load', navigation.loadEventEnd - navigation.startTime],
        ];
        for (const [stage, value] of values) {
          this.reportDuration('page.performance', value, { stage, navigation_type: navigation.type });
        }
      }

      const resources = (performance.getEntriesByType('resource') as PerformanceResourceTiming[])
        .filter((entry) => {
          try {
            const resource = new URL(entry.name, window.location.href);
            return resource.origin === window.location.origin && ['script', 'link', 'css'].includes(entry.initiatorType);
          } catch {
            return false;
          }
        })
        .sort((left, right) => right.duration - left.duration)
        .slice(0, 5);
      for (const resource of resources) {
        this.reportDuration(
          'resource.performance',
          resource.duration,
          { resource_type: resource.initiatorType || 'unknown' },
          this.safeRoute(resource.name),
        );
      }

      const firstContentfulPaint = performance
        .getEntriesByName('first-contentful-paint')[0];
      if (firstContentfulPaint) {
        this.reportDuration('page.performance', firstContentfulPaint.startTime, { stage: 'first_contentful_paint' });
      }
    };

    if (document.readyState === 'complete') {
      window.setTimeout(collect, 0);
    } else {
      window.addEventListener('load', () => window.setTimeout(collect, 0), { once: true });
    }
  }

  private reportDuration(
    name: string,
    duration: number,
    details: Record<string, string>,
    route = this.safeRoute(window.location.href),
  ): void {
    if (!Number.isFinite(duration) || duration < 0 || duration > MAX_EVENT_DURATION_MS) return;
    this.report({ name, route, duration_ms: Math.round(duration), details });
  }

  private installLifecycleObservers(): void {
    window.addEventListener('online', () => {
      this.reportNetworkChange('online');
      this.scheduleFlush(0);
    });
    window.addEventListener('offline', () => this.reportNetworkChange('offline'));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.report({ name: 'app.backgrounded', details: { visibility: 'hidden' } });
        void this.flushQueue(true);
      } else {
        this.report({ name: 'app.foregrounded', details: { visibility: 'visible' } });
        this.scheduleFlush(0);
      }
    });
    window.addEventListener('pagehide', (event: PageTransitionEvent) => {
      this.report({
        name: 'app.terminated',
        details: { state: event.persisted ? 'bfcache' : 'pagehide' },
      });
      void this.flushQueue(true);
    });
    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
      if (event.persisted) {
        this.report({ name: 'app.foregrounded', details: { state: 'bfcache' } });
        this.scheduleFlush(0);
      }
    });
    this.connection()?.addEventListener('change', () => this.reportNetworkChange('connection_change'));
    window.addEventListener('storage', (event) => {
      if (event.key === QUEUE_KEY && event.newValue) this.scheduleFlush(250);
    });
  }

  private reportNetworkChange(trigger: string): void {
    const current = this.connectionType();
    const previous = this.previousNetwork;
    this.previousNetwork = current;
    this.report({
      name: 'network.changed',
      details: { from: previous, to: current, trigger },
      online: navigator.onLine,
    });
  }

  private scheduleFlush(delayMs: number): void {
    if (!runtimeConfig.telemetryEnabled) return;
    if (this.flushTimer !== undefined) window.clearTimeout(this.flushTimer);
    this.flushTimer = window.setTimeout(() => {
      this.flushTimer = undefined;
      void this.flushQueue(false);
    }, delayMs);
  }

  private async flushQueue(keepalive: boolean): Promise<void> {
    if (this.flushing || !navigator.onLine || !this.acquireFlushLock()) return;
    this.flushing = true;
    try {
      const now = Date.now();
      const queue = this.readQueue();
      const first = queue.find((item) => item.nextAttemptAt <= now);
      if (!first) {
        const next = queue.reduce((minimum, item) => Math.min(minimum, item.nextAttemptAt), Number.POSITIVE_INFINITY);
        if (Number.isFinite(next)) this.scheduleFlush(Math.max(250, next - now));
        return;
      }
      const batch = queue
        .filter((item) => item.nextAttemptAt <= now && this.sameHeaders(item.headers, first.headers))
        .slice(0, this.batchSize);
      const controller = typeof AbortController === 'function' ? new AbortController() : undefined;
      const timeout = controller ? window.setTimeout(() => controller.abort(), SEND_TIMEOUT_MS) : undefined;
      let response: Response | undefined;
      try {
        response = await fetch(this.endpoint.href, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...first.headers },
          body: JSON.stringify({ events: batch.map((item) => item.event) }),
          keepalive,
          credentials: 'omit',
          signal: controller?.signal,
        });
      } catch {
        // Retried below with bounded exponential backoff.
      } finally {
        if (timeout !== undefined) window.clearTimeout(timeout);
      }

      const ids = new Set(batch.map((item) => item.id));
      if (response?.ok) {
        this.updateQueue((current) => current.filter((item) => !ids.has(item.id)));
        this.batchSize = Math.min(MAX_BATCH_LENGTH, this.batchSize * 2);
        if (this.readQueue().length > 0) {
          this.scheduleFlush(50);
        }
        return;
      }
      if (response && [400, 422].includes(response.status)) {
        // During a rolling deploy one new event/schema field must not destroy 49 valid
        // neighbours. Bisect only explicit schema rejections and cap the extra calls.
        if (batch.length > 1 && this.takeSchemaSplitBudget()) {
          this.batchSize = Math.max(1, Math.floor(batch.length / 2));
        } else {
          this.updateQueue((current) => current.filter((item) => !ids.has(item.id)));
          this.batchSize = Math.min(MAX_BATCH_LENGTH, this.batchSize * 2);
        }
        if (this.readQueue().length > 0) {
          this.scheduleFlush(50);
        }
        return;
      }
      if (response && response.status >= 400 && response.status < 500 && ![408, 429].includes(response.status)) {
        // Authentication, routing and other permanent client errors affect the whole
        // request; splitting such a batch would only amplify traffic during an outage.
        this.updateQueue((current) => current.filter((item) => !ids.has(item.id)));
        this.batchSize = MAX_BATCH_LENGTH;
        if (this.readQueue().length > 0) {
          this.scheduleFlush(50);
        }
        return;
      }

      const delay = this.retryDelay(batch[0]?.attempts ?? 0, response);
      this.updateQueue((current) => current.map((item) => ids.has(item.id)
        ? { ...item, attempts: Math.min(item.attempts + 1, 20), nextAttemptAt: Date.now() + delay }
        : item));
      this.scheduleFlush(delay);
    } finally {
      this.flushing = false;
      this.releaseFlushLock();
    }
  }

  private retryDelay(attempt: number, response?: Response): number {
    const retryAfter = response?.headers.get('Retry-After');
    if (retryAfter && /^\d+$/.test(retryAfter)) {
      return Math.min(5 * 60_000, Number(retryAfter) * 1_000);
    }
    const exponential = Math.min(5 * 60_000, 5_000 * 2 ** Math.min(attempt, 6));
    return Math.round(exponential * (0.8 + Math.random() * 0.4));
  }

  private takeSchemaSplitBudget(): boolean {
    const now = Date.now();
    let budget = this.readSchemaSplitBudget();
    if (budget.windowStartedAt === 0
      || now < budget.windowStartedAt
      || now - budget.windowStartedAt >= SCHEMA_SPLIT_WINDOW_MS) {
      budget = { windowStartedAt: now, attempts: 0 };
    }
    if (budget.attempts >= MAX_SCHEMA_SPLITS_PER_WINDOW) return false;
    this.writeSchemaSplitBudget({ ...budget, attempts: budget.attempts + 1 });
    return true;
  }

  private readSchemaSplitBudget(): SchemaSplitBudget {
    try {
      const parsed = JSON.parse(localStorage.getItem(SCHEMA_SPLIT_BUDGET_KEY) ?? 'null') as Partial<SchemaSplitBudget> | null;
      if (parsed
        && typeof parsed.windowStartedAt === 'number'
        && Number.isFinite(parsed.windowStartedAt)
        && typeof parsed.attempts === 'number'
        && Number.isInteger(parsed.attempts)
        && parsed.attempts >= 0) {
        this.schemaSplitBudget = {
          windowStartedAt: parsed.windowStartedAt,
          attempts: parsed.attempts,
        };
      }
    } catch {
      // Privacy mode uses the per-tab in-memory fallback below.
    }
    return this.schemaSplitBudget;
  }

  private writeSchemaSplitBudget(budget: SchemaSplitBudget): void {
    this.schemaSplitBudget = budget;
    try {
      localStorage.setItem(SCHEMA_SPLIT_BUDGET_KEY, JSON.stringify(budget));
    } catch {
      // The in-memory budget still bounds this tab when persistent storage is disabled.
    }
  }

  private appendToQueue(item: QueuedTelemetryEvent): void {
    this.updateQueue((queue) => [...queue, item].slice(-MAX_QUEUE_LENGTH));
  }

  private updateQueue(update: (queue: QueuedTelemetryEvent[]) => QueuedTelemetryEvent[]): void {
    const next = update(this.readQueue()).slice(-MAX_QUEUE_LENGTH);
    this.memoryQueue = next;
    try {
      if (next.length === 0) {
        localStorage.removeItem(QUEUE_KEY);
      } else {
        localStorage.setItem(QUEUE_KEY, JSON.stringify(next));
      }
      this.memoryQueue = [];
    } catch {
      // Keep a bounded best-effort queue for this page when storage is unavailable.
    }
  }

  private readQueue(): QueuedTelemetryEvent[] {
    const oldest = Date.now() - MAX_QUEUE_AGE_MS;
    let persistent: QueuedTelemetryEvent[] = [];
    try {
      const parsed: unknown = JSON.parse(localStorage.getItem(QUEUE_KEY) ?? '[]');
      if (!Array.isArray(parsed)) throw new Error('invalid queue');
      persistent = parsed.filter(
        (item): item is QueuedTelemetryEvent => this.isQueuedItem(item) && item.createdAt >= oldest,
      );
    } catch {
      this.removeStorage(QUEUE_KEY);
    }
    const combined = new Map<string, QueuedTelemetryEvent>();
    for (const item of persistent) combined.set(item.id, item);
    for (const item of this.memoryQueue) {
      if (item.createdAt >= oldest) combined.set(item.id, item);
    }
    return Array.from(combined.values()).slice(-MAX_QUEUE_LENGTH);
  }

  private isQueuedItem(candidate: unknown): candidate is QueuedTelemetryEvent {
    if (!candidate || typeof candidate !== 'object') return false;
    const item = candidate as Partial<QueuedTelemetryEvent>;
    return typeof item.id === 'string'
      && typeof item.createdAt === 'number'
      && typeof item.attempts === 'number'
      && typeof item.nextAttemptAt === 'number'
      && !!item.headers
      && typeof item.headers === 'object'
      && this.isClientTelemetryEvent(item.event);
  }

  private isClientTelemetryEvent(candidate: unknown): candidate is ClientTelemetryEvent {
    if (!candidate || typeof candidate !== 'object') return false;
    const event = candidate as Partial<ClientTelemetryEvent>;
    return typeof event.name === 'string' && ALLOWED_EVENT_NAMES.has(event.name);
  }

  private acquireFlushLock(): boolean {
    try {
      const now = Date.now();
      const raw = localStorage.getItem(FLUSH_LOCK_KEY);
      const current = raw ? JSON.parse(raw) as { owner?: string; expiresAt?: number } : undefined;
      if (current?.owner !== this.ownerId && (current?.expiresAt ?? 0) > now) return false;
      localStorage.setItem(FLUSH_LOCK_KEY, JSON.stringify({ owner: this.ownerId, expiresAt: now + SEND_TIMEOUT_MS + 2_000 }));
      const stored = JSON.parse(localStorage.getItem(FLUSH_LOCK_KEY) ?? '{}') as { owner?: string };
      return stored.owner === this.ownerId;
    } catch {
      return true;
    }
  }

  private releaseFlushLock(): void {
    try {
      const current = JSON.parse(localStorage.getItem(FLUSH_LOCK_KEY) ?? '{}') as { owner?: string };
      if (current.owner === this.ownerId) localStorage.removeItem(FLUSH_LOCK_KEY);
    } catch {
      this.removeStorage(FLUSH_LOCK_KEY);
    }
  }

  private sameHeaders(left: Record<string, string>, right: Record<string, string>): boolean {
    const keys = Object.keys(left);
    return keys.length === Object.keys(right).length && keys.every((key) => left[key] === right[key]);
  }

  private sameUrl(left: URL, right: URL): boolean {
    return left.origin === right.origin && left.pathname === right.pathname;
  }

  private getOrCreateDeviceId(): string {
    const key = 'reforce_installation_id';
    const earlyDeviceId = canonicalUuid(window.__reforceObservability?.context?.deviceId);
    if (earlyDeviceId) return earlyDeviceId;
    try {
      const current = canonicalUuid(localStorage.getItem(key));
      if (current) return current;
      const created = this.newCorrelationId();
      localStorage.setItem(key, created);
      return created;
    } catch {
      return this.newCorrelationId();
    }
  }

  private getFlowId(): string {
    const key = 'reforce_auth_flow_id';
    const sessionKey = 'reforce_auth_session_id';
    const query = canonicalUuid(this.readQueryValue('flow_id'));
    const authSession = canonicalUuid(this.readQueryValue('session_id'));
    const earlyFlowId = window.__reforceObservability?.context?.flowId;
    const safeEarlyFlowId = canonicalUuid(earlyFlowId);
    if (safeEarlyFlowId) return safeEarlyFlowId;
    try {
      const stored = sessionStorage.getItem(key);
      const storedAuthSession = sessionStorage.getItem(sessionKey);
      const isNewAuthSession = !!authSession && authSession !== storedAuthSession;
      const flowId = query
        ?? (isNewAuthSession ? this.newCorrelationId() : canonicalUuid(stored) ?? this.newCorrelationId());
      sessionStorage.setItem(key, flowId);
      if (authSession) {
        sessionStorage.setItem(sessionKey, authSession);
      }
      return flowId;
    } catch {
      return query ?? this.newCorrelationId();
    }
  }

  private newCorrelationId(): string {
    try {
      if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      return this.formatUuid(bytes);
    } catch {
      const bytes = new Uint8Array(16);
      for (let index = 0; index < bytes.length; index += 1) {
        bytes[index] = Math.floor(Math.random() * 256);
      }
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      return this.formatUuid(bytes);
    }
  }

  private formatUuid(bytes: Uint8Array): string {
    const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  private readQueryValue(name: string): string | null {
    try {
      return new URLSearchParams(window.location.search).get(name);
    } catch {
      return null;
    }
  }

  private resolveUrl(value: string): URL {
    try {
      return new URL(value || '/', window.location.origin);
    } catch {
      return new URL('/', window.location.origin);
    }
  }

  private connection(): ConnectionInformation | undefined {
    return (navigator as Navigator & { connection?: ConnectionInformation }).connection;
  }

  private connectionType(): string {
    if (!navigator.onLine) return 'offline';
    const value = this.connection()?.effectiveType ?? this.connection()?.type ?? 'unknown';
    return ALLOWED_NETWORK_VALUES.has(value) ? value : 'unknown';
  }

  private normalizeDetails(details?: Record<string, string>): Record<string, string> | null | undefined {
    if (!details) return undefined;
    const entries = Object.entries(details);
    if (entries.length > 20) return null;
    const normalized: Record<string, string> = {};
    for (const [key, value] of entries) {
      const pattern = DETAIL_VALUE_PATTERNS[key];
      if (!pattern || !pattern.test(value)) return null;
      normalized[key] = value;
    }
    return normalized;
  }

  private elapsed(startedAt: number): number {
    return Math.max(0, Math.round(performance.now() - startedAt));
  }

  private removeStorage(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage can be disabled in privacy mode.
    }
  }
}
